import * as Sentry from '@sentry/react-native';
import Constants from 'expo-constants';
import * as Updates from 'expo-updates';
import { Platform } from 'react-native';

import { colors } from '@/src/theme/colors';

const SENTRY_DSN =
  'https://10ad73c0aec3cf3d5f7957e14b20794a@o4510670640185344.ingest.us.sentry.io/4511365516886016';

const PRODUCTION_ENVIRONMENT = 'production';
const APP_DISTRIBUTION_ENVIRONMENT = 'app-distribution';

const productionSampling = {
  profilesSampleRate: 0.1,
  replaysOnErrorSampleRate: 1,
  replaysSessionQuality: 'medium' as const,
  replaysSessionSampleRate: 0.1,
  tracesSampleRate: 0.2,
};

const highSignalSampling = {
  profilesSampleRate: 1,
  replaysOnErrorSampleRate: 1,
  replaysSessionQuality: 'high' as const,
  replaysSessionSampleRate: 1,
  tracesSampleRate: 1,
};

const sensitiveKeyPattern =
  /(access[_-]?token|auth|body|caption|comment|content|downloadurl|email|gif|message|password|post|query|search|text|token|url)/i;
const emailPattern = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const secretQueryPattern =
  /([?&](?:access_token|auth|code|key|token|X-Goog-Signature|X-Goog-Credential|X-Goog-Algorithm|X-Goog-Date|X-Goog-Expires|X-Goog-SignedHeaders)=)[^&#\s]+/gi;
const maxSanitizedStringLength = 500;

let sentryInitialized = false;

type SentryIdentity = {
  email?: string | null;
  fullName?: string | null;
  uid: string;
  username?: string | null;
};

type SentryFeedbackInput = {
  email?: string | null;
  message: string;
  name?: string | null;
};

function getSentryEnvironment() {
  if (__DEV__) {
    return 'development';
  }

  if (Updates.channel === APP_DISTRIBUTION_ENVIRONMENT) {
    return APP_DISTRIBUTION_ENVIRONMENT;
  }

  return PRODUCTION_ENVIRONMENT;
}

function isHighSignalEnvironment(environment: string) {
  return (
    environment === 'development' ||
    environment === APP_DISTRIBUTION_ENVIRONMENT
  );
}

function shouldInitializeSentry(environment: string) {
  if (environment !== 'development') {
    return true;
  }

  return process.env.EXPO_PUBLIC_ENABLE_SENTRY_DEV === 'true';
}

function getSentryRelease() {
  const configuredRelease = Constants.expoConfig?.extra?.sentryRelease;

  if (typeof configuredRelease === 'string' && configuredRelease.trim()) {
    return configuredRelease.trim();
  }

  const version = Constants.expoConfig?.version ?? 'unknown';

  return `frienzzi@${version}`;
}

function getSentryDist() {
  const dist =
    Updates.updateId ?? Updates.runtimeVersion ?? Updates.channel ?? 'web';

  return typeof dist === 'string' && dist.trim() ? dist.trim() : 'web';
}

function stripSensitiveQueryValues(value: string) {
  return value.replace(secretQueryPattern, '$1[redacted]');
}

function sanitizeUrl(value: string) {
  try {
    const parsed = new URL(value);

    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return stripSensitiveQueryValues(value);
  }
}

function sanitizeString(value: string, key = '') {
  let nextValue = value;
  const isFeedbackContactField =
    key === 'contexts.feedback.contact_email' || key === 'contexts.feedback.name';
  const isFeedbackMessageField = key === 'contexts.feedback.message';
  const isSafeDiagnosticField = /(^|\.)error_context$/.test(key);

  if (/https?:\/\//i.test(nextValue)) {
    nextValue = nextValue.replace(/https?:\/\/[^\s)]+/gi, (match) =>
      sanitizeUrl(match),
    );
  }

  nextValue = stripSensitiveQueryValues(nextValue);

  if (key !== 'user.email' && !isFeedbackContactField) {
    nextValue = nextValue.replace(emailPattern, '[email]');
  }

  if (
    sensitiveKeyPattern.test(key) &&
    !isFeedbackMessageField &&
    !isSafeDiagnosticField
  ) {
    if (/url/i.test(key)) {
      nextValue = sanitizeUrl(nextValue);
    } else if (!/^(message|category|type|level|route|screen|transaction)$/i.test(key)) {
      nextValue = '[redacted]';
    }
  }

  if (nextValue.length > maxSanitizedStringLength) {
    return `${nextValue.slice(0, maxSanitizedStringLength)}...`;
  }

  return nextValue;
}

function sanitizeUnknown(value: unknown, key = '', depth = 0): unknown {
  if (value == null) {
    return value;
  }

  if (typeof value === 'string') {
    return sanitizeString(value, key);
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return value;
  }

  if (depth >= 4) {
    return '[truncated]';
  }

  if (Array.isArray(value)) {
    return value.slice(0, 50).map((item, index) =>
      sanitizeUnknown(item, `${key}.${index}`, depth + 1),
    );
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(
        ([entryKey, entryValue]) => [
          entryKey,
          sanitizeUnknown(
            entryValue,
            key ? `${key}.${entryKey}` : entryKey,
            depth + 1,
          ),
        ],
      ),
    );
  }

  return String(value);
}

type SentryExceptionValue = {
  rawStacktrace?: {
    frames?: unknown[];
  } | null;
  stacktrace?: {
    frames?: unknown[];
  } | null;
  type?: unknown;
  value?: unknown;
};

type SentryEventLike = {
  breadcrumbs?: {
    data?: Record<string, unknown> | null;
    message?: unknown;
  }[];
  contexts?: unknown;
  exception?: {
    values?: SentryExceptionValue[];
  };
  message?: unknown;
  request?: {
    url?: unknown;
  } | null;
};

type SentryLogLike = {
  attributes?: unknown;
  body?: unknown;
};

function hasStacktraceFrames(exception: SentryExceptionValue) {
  return Boolean(
    exception.stacktrace?.frames?.length ||
      exception.rawStacktrace?.frames?.length,
  );
}

function stackFrameHasTextMatching(frame: unknown, pattern: RegExp) {
  if (!frame || typeof frame !== 'object') {
    return false;
  }

  const data = frame as Record<string, unknown>;

  return ['absPath', 'filename', 'module'].some((key) => {
    const value = data[key];

    return typeof value === 'string' && pattern.test(value);
  });
}

function exceptionHasStackFrameMatching(
  exception: SentryExceptionValue,
  pattern: RegExp,
) {
  const frames = [
    ...(exception.stacktrace?.frames ?? []),
    ...(exception.rawStacktrace?.frames ?? []),
  ];

  return frames.some((frame) => stackFrameHasTextMatching(frame, pattern));
}

function isNoStackIndexedDbConnectionLoss(exception: SentryExceptionValue) {
  const value = typeof exception.value === 'string' ? exception.value : '';

  return (
    /Connection to Indexed Database server lost/i.test(value) &&
    !hasStacktraceFrames(exception)
  );
}

function eventHasUrlMatching(event: SentryEventLike, pattern: RegExp) {
  const requestUrl = event.request?.url;

  if (typeof requestUrl === 'string' && pattern.test(requestUrl)) {
    return true;
  }

  return Boolean(
    event.breadcrumbs?.some((breadcrumb) => {
      const breadcrumbUrl = breadcrumb.data?.url;

      return typeof breadcrumbUrl === 'string' && pattern.test(breadcrumbUrl);
    }),
  );
}

function isLocalDevelopmentUrl(value: unknown) {
  if (typeof value !== 'string') {
    return false;
  }

  return /^https?:\/\/(?:localhost|127\.0\.0\.1|\[::1\])(?::\d+)?(?:\/|$)/i.test(
    value,
  );
}

function eventHasLocalRequestUrl(event: SentryEventLike) {
  if (isLocalDevelopmentUrl(event.request?.url)) {
    return true;
  }

  return Boolean(
    event.breadcrumbs?.some((breadcrumb) =>
      isLocalDevelopmentUrl(breadcrumb.data?.url),
    ),
  );
}

function isNoStackExpectedNoise(
  exception: SentryExceptionValue,
  event: SentryEventLike,
) {
  if (hasStacktraceFrames(exception)) {
    return false;
  }

  const value = typeof exception.value === 'string' ? exception.value : '';
  const type = typeof exception.type === 'string' ? exception.type : '';

  return (
    /auth\/email-already-in-use/i.test(value) ||
    /auth\/expired-action-code/i.test(value) ||
    /auth\/invalid-action-code/i.test(value) ||
    /auth\/popup-closed-by-user/i.test(value) ||
    /auth\/too-many-requests/i.test(value) ||
    /Facebook sign-in was canceled/i.test(value) ||
    /Browser notification permission was not granted/i.test(value) ||
    /Clipboard copy is not available on this device/i.test(value) ||
    /Your browser blocked clipboard access/i.test(value) ||
    /Make a change before saving/i.test(value) ||
    /Videos must be 1GB or smaller/i.test(value) ||
    /This post is no longer available/i.test(value) ||
    /Missing or insufficient permissions/i.test(value) ||
    /deadline-exceeded/i.test(value) ||
    /storage\/retry-limit-exceeded/i.test(value) ||
    /Connection to Indexed Database server lost/i.test(value) ||
    /IndexedDB transaction 'Allocate target' failed/i.test(value) ||
    /Database deleted by request of the user/i.test(value) ||
    /internal error was encountered in the Indexed Database server/i.test(
      value,
    ) ||
    /Failed to delete record from object store/i.test(value) ||
    /Attempt to delete range from database without an in-progress transaction/i.test(
      value,
    ) ||
    (/FIRESTORE .* INTERNAL ASSERTION FAILED/i.test(value) &&
      /Indexed|database|object store|transaction|e\.Eu\.get/i.test(value)) ||
    /^Params are not set\.?$/i.test(value) ||
    (type === 'NotSupportedError' &&
      /^The operation is not supported\.?$/i.test(value)) ||
    (/^HTTP Client Error with status code: 503$/i.test(value) &&
      eventHasUrlMatching(event, /firebaseinstallations\.googleapis\.com/i)) ||
    /^AbortError:\s*AbortError\.?$/i.test(value) ||
    /^AbortError:\s*The operation was aborted\.?$/i.test(value) ||
    /^The operation was aborted\.?$/i.test(value)
  );
}

function isExpectedExceptionNoise(
  exception: SentryExceptionValue,
  event: SentryEventLike,
) {
  const value = typeof exception.value === 'string' ? exception.value : '';

  return (
    /Invalid call to runtime\.sendMessage\(\)\. Tab not found/i.test(value) ||
    /^Your email is not verified yet\.?$/i.test(value) ||
    /^Connect to the internet to finish onboarding\.?$/i.test(value) ||
    /^FirebaseError:\s*internal$/i.test(value) ||
    /^internal$/i.test(value) ||
    /^Params are not set\.?$/i.test(value) ||
    (/Error invoking enableButtonsClickedMetaDataLogging: Java object is gone/i.test(
      value,
    ) &&
      exceptionHasStackFrameMatching(
        exception,
        /iabjs:\/\/navigation_performance_logger_android/i,
      )) ||
    (/^HTTP Client Error with status code: 503$/i.test(value) &&
      eventHasUrlMatching(event, /firebaseinstallations\.googleapis\.com/i)) ||
    (/^Load failed(?:\s*\([^)]*\))?$/i.test(value) &&
      eventHasUrlMatching(event, /bigdatacloud\.net|google-analytics\.com/i))
  );
}

function isRecaptchaThirdPartyNoise(exception: SentryExceptionValue) {
  const value = typeof exception.value === 'string' ? exception.value : '';

  return (
    /null is not an object \(evaluating ['"]?D\.style['"]?\)/i.test(value) &&
    exceptionHasStackFrameMatching(exception, /recaptcha(?:__|\.|$)/i)
  );
}

function isKnownBrowserConsoleNoise(value: string) {
  return (
    /Blocked a frame with origin .* from accessing a frame with origin/i.test(
      value,
    ) ||
    /Protocols, domains, and ports must match/i.test(value) ||
    /The script has an unsupported MIME type \(['"]text\/html['"]\)/i.test(
      value,
    ) ||
    /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i.test(
      value,
    ) ||
    /^(?:In HTML, )?<button> cannot (?:contain|be a descendant of) a nested <button>/i.test(
      value,
    ) ||
    /"shadow\*" style props are deprecated/i.test(value) ||
    /"textShadow\*" style props are deprecated/i.test(value) ||
    /props\.pointerEvents is deprecated/i.test(value)
  );
}

function unknownToNoiseText(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (value == null) {
    return '';
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

function shouldDropLog(log: SentryLogLike) {
  const body = unknownToNoiseText(log.body);
  const attributes = unknownToNoiseText(log.attributes);

  return (
    isKnownBrowserConsoleNoise(body) ||
    isKnownBrowserConsoleNoise(attributes)
  );
}

function shouldDropEvent(event: SentryEventLike) {
  const eventMessage =
    typeof event.message === 'string' ? event.message : '';

  if (eventHasLocalRequestUrl(event)) {
    return true;
  }

  if (isKnownBrowserConsoleNoise(eventMessage)) {
    return true;
  }

  return Boolean(
    event.exception?.values?.some((exception) => {
      const value =
        typeof exception.value === 'string' ? exception.value : '';

      return (
        isNoStackIndexedDbConnectionLoss(exception) ||
        isExpectedExceptionNoise(exception, event) ||
        isNoStackExpectedNoise(exception, event) ||
        isRecaptchaThirdPartyNoise(exception) ||
        isKnownBrowserConsoleNoise(value) ||
        /Can't find variable: EmptyRanges/i.test(value) ||
        /window\.webkit\.messageHandlers/i.test(value)
      );
    }),
  );
}

function sanitizeEvent<
  T extends {
    contexts?: unknown;
    exception?: unknown;
    threads?: unknown;
    user?: unknown;
  },
>(
  event: T,
): T | null {
  if (shouldDropEvent(event as SentryEventLike)) {
    return null;
  }

  const sanitized = sanitizeUnknown(event) as T;

  if (event.user) {
    sanitized.user = event.user;
  }

  if (event.contexts) {
    sanitized.contexts = {
      ...(sanitized.contexts && typeof sanitized.contexts === 'object'
        ? sanitized.contexts
        : {}),
      trace:
        typeof event.contexts === 'object' &&
        event.contexts !== null &&
        'trace' in event.contexts
          ? (event.contexts as { trace?: unknown }).trace
          : undefined,
    };
  }

  if (event.exception) {
    sanitized.exception = event.exception;
  }

  if (event.threads) {
    sanitized.threads = event.threads;
  }

  return sanitized;
}

function sanitizeLog<T extends { attributes?: unknown; body?: unknown }>(
  log: T,
): T | null {
  if (shouldDropLog(log)) {
    return null;
  }

  return {
    ...log,
    attributes: sanitizeUnknown(log.attributes, 'attributes'),
    body: sanitizeUnknown(log.body, 'body'),
  };
}

function sanitizeBreadcrumb(
  breadcrumb: Parameters<
    NonNullable<Sentry.ReactNativeOptions['beforeBreadcrumb']>
  >[0],
) {
  return {
    ...breadcrumb,
    data: sanitizeUnknown(breadcrumb.data, 'breadcrumb.data') as
      | Record<string, unknown>
      | undefined,
    message:
      typeof breadcrumb.message === 'string'
        ? sanitizeString(breadcrumb.message, 'breadcrumb.message')
        : breadcrumb.message,
  };
}

function getReplayIntegration() {
  if (Platform.OS === 'web') {
    return Sentry.browserReplayIntegration({
      maskAllInputs: true,
      maskAllText: true,
      blockAllMedia: true,
    });
  }

  return Sentry.mobileReplayIntegration({
    beforeErrorSampling: () => true,
    maskAllImages: true,
    maskAllText: true,
    maskAllVectors: true,
    screenshotStrategy: 'pixelCopy',
  });
}

function getFeedbackIntegration() {
  return Sentry.feedbackIntegration({
    addScreenshotButtonLabel: 'Add screenshot',
    cancelButtonLabel: 'Cancel',
    colorScheme: 'light',
    emailError: 'Enter a valid email address.',
    emailLabel: 'Email',
    enableScreenshot: true,
    enableTakeScreenshot: true,
    errorTitle: 'Unable to send report',
    formError: 'Please add a short message before sending.',
    formTitle: 'Report a problem',
    genericError: 'Unable to send your report right now.',
    isEmailRequired: false,
    isNameRequired: false,
    isRequiredLabel: 'Required',
    messageLabel: 'What happened?',
    messagePlaceholder: 'Tell us what broke or felt off.',
    nameLabel: 'Name',
    removeScreenshotButtonLabel: 'Remove screenshot',
    showBranding: false,
    showEmail: true,
    showName: true,
    shouldValidateEmail: true,
    submitButtonLabel: 'Send report',
    successMessageText: 'Thanks. Your report was sent.',
    themeLight: {
      accentBackground: colors.primary,
      accentForeground: colors.surface,
      background: colors.surface,
      border: colors.border,
      feedbackIcon: colors.primary,
      foreground: colors.text,
    },
    useSentryUser: {
      email: 'email',
      name: 'name',
    },
  });
}

export function initSentry() {
  if (sentryInitialized) {
    return;
  }

  const environment = getSentryEnvironment();

  if (!shouldInitializeSentry(environment)) {
    return;
  }

  const sampling = isHighSignalEnvironment(environment)
    ? highSignalSampling
    : productionSampling;

  Sentry.init({
    dsn: SENTRY_DSN,
    attachAllThreads: true,
    attachScreenshot: true,
    attachStacktrace: true,
    attachThreads: true,
    attachViewHierarchy: true,
    beforeBreadcrumb: (breadcrumb) => sanitizeBreadcrumb(breadcrumb),
    beforeSend: (event) => sanitizeEvent(event),
    beforeSendLog: (log) => sanitizeLog(log),
    beforeSendTransaction: (event) => sanitizeEvent(event),
    dist: getSentryDist(),
    enableAnrFingerprinting: true,
    enableAppHangTracking: true,
    enableAppStartTracking: true,
    enableAutoPerformanceTracing: true,
    enableAutoSessionTracking: true,
    enableCaptureFailedRequests: true,
    enableLogs: true,
    enableNativeCrashHandling: true,
    enableNativeFramesTracking: true,
    enableNdk: true,
    enableNdkScopeSync: true,
    enableStallTracking: true,
    enableTombstone: true,
    enableUserInteractionTracing: true,
    enableWatchdogTerminationTracking: true,
    environment,
    ignoreSpans: [
      /api\.giphy\.com/i,
      /bigdatacloud\.net/i,
      /facebook\.com/i,
      /graph\.facebook\.com/i,
      /open-meteo\.com/i,
      /sentry\.io/i,
    ],
    integrations: (defaultIntegrations) => [
      ...defaultIntegrations,
      getReplayIntegration(),
      getFeedbackIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],
    logsOrigin: 'all',
    maxBreadcrumbs: 100,
    maxValueLength: maxSanitizedStringLength,
    profilesSampleRate: sampling.profilesSampleRate,
    propagateTraceparent: false,
    release: getSentryRelease(),
    replaysOnErrorSampleRate: sampling.replaysOnErrorSampleRate,
    replaysSessionQuality: sampling.replaysSessionQuality,
    replaysSessionSampleRate: sampling.replaysSessionSampleRate,
    sampleRate: 1,
    screenshot: {
      maskAllImages: true,
      maskAllText: true,
    },
    sendDefaultPii: true,
    tracesSampleRate: sampling.tracesSampleRate,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/frienzzi\.com\//,
      /^https:\/\/frienzzi\.web\.app\//,
    ],
    _experiments: {
      profilingOptions: {
        lifecycle: 'trace',
        profileSessionSampleRate: sampling.profilesSampleRate,
        startOnAppStart: true,
      },
    },
  });

  Sentry.setTag('app', 'frienzzi');
  Sentry.setTag('platform', Platform.OS);
  sentryInitialized = true;
}

export function setSentryUserIdentity(identity: SentryIdentity | null) {
  if (!identity) {
    Sentry.setUser(null);
    Sentry.setTag('signed_in', 'false');
    Sentry.setTag('username', '');
    return;
  }

  Sentry.setUser({
    email: identity.email ?? undefined,
    id: identity.uid,
    name: identity.fullName ?? undefined,
    username: identity.username ?? undefined,
  });
  Sentry.setTag('signed_in', 'true');
  Sentry.setTag('username', identity.username ?? '');
}

export function setSentryRoute(pathname: string) {
  const route = sanitizeString(pathname, 'route');

  Sentry.setTag('route', route);
  Sentry.setContext('navigation', { route });
  Sentry.addBreadcrumb({
    category: 'navigation',
    level: 'info',
    message: `screen:${route}`,
  });
  Sentry.getCurrentReactNativeTracingIntegration()?.setCurrentRoute(route);
}

export function recordSentryError(error: unknown, context?: string) {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setTag('error_context', sanitizeString(context, 'error_context'));
    }

    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
    );
  });
}

function getFeedbackUrl() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return undefined;
  }

  return sanitizeUrl(window.location.href);
}

export async function submitSentryFeedback({
  email,
  message,
  name,
}: SentryFeedbackInput) {
  const eventId = Sentry.captureFeedback(
    {
      email: email?.trim() || undefined,
      message: message.trim(),
      name: name?.trim() || undefined,
      source: 'settings_report_problem',
      tags: {
        app: 'frienzzi',
        source: 'settings_report_problem',
      },
      url: getFeedbackUrl(),
    },
    { includeReplay: true },
  );

  await Promise.race([
    Sentry.flush(),
    new Promise((resolve) => {
      setTimeout(resolve, 2000);
    }),
  ]);

  return eventId;
}

export function sendSentryTestError() {
  const error = new Error('Frienzzi Sentry diagnostics test error');

  Sentry.withScope((scope) => {
    scope.setTag('diagnostics_test', 'true');
    scope.setLevel('error');
    Sentry.captureException(error);
  });
}
